import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaUserMd, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { FiLogOut } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import api from '../../api/axios';

const DoctorLogin = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username || !password) {
      setError(t('auth.error_empty'));
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const res = await api.post('/auth/login/doctor', formData.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (res.status === 200) {
        const data = res.data;
        login(data.access_token, 'doctor', 'doctor');
        localStorage.setItem('doctor_email', username);
        navigate('/doctor/dashboard');
      } else {
        setError(t('auth.error_failed'));
      }
    } catch (err: any) {
      const respData = err?.response?.data;
      const errorCode = respData?.code;
      const backendMsg = respData?.message;
      const i18nKey = errorCode ? `auth.${errorCode.toLowerCase()}` : null;

      if (i18nKey && t(i18nKey) !== i18nKey) {
        setError(t(i18nKey));
      } else if (backendMsg) {
        setError(backendMsg);
      } else {
        setError(t('auth.error_network'));
      }
    }
  };



  const shakeAnimation = {
    x: [0, -10, 10, -10, 10, 0],
    transition: { duration: 0.6 },
  };

  const doctorEmail = localStorage.getItem('doctor_email') || 'doctor@example.com';

  return (
    <div
      className="min-h-screen bg-background bg-gradient-hero text-base"
    >
      <Helmet>
        <title>{t('auth.doctor_login_title')} - Melodis Clinic</title>
      </Helmet>

      {/* 顶部导航栏 */}
      <div className="flex justify-between items-center px-4 sm:px-6 py-4 bg-white bg-opacity-90 shadow">
        <div className="flex items-center gap-4">
          <img
            src="/images/Melodislogo.png"
            alt="Logo"
            className="h-10 cursor-pointer"
            onClick={() => navigate('/doctor/dashboard')}
          />
          <h1 className="text-xl sm:text-2xl font-bold">{t('doctor.title')}</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm max-w-[120px] truncate">
            {t('doctor.welcome', { email: doctorEmail })}
          </span>
          <button onClick={() => i18n.changeLanguage('ja')} className="text-sm underline">
            日本語
          </button>
          <button onClick={() => i18n.changeLanguage('zh')} className="text-sm underline">
            中文
            </button>
        </div>
      </div>

      {/* 登录表单 */}
      <div className="flex justify-center px-4 mt-10">
        <motion.div
          className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md"
          animate={error ? shakeAnimation : {}}
        >
          <h2 className="text-2xl font-bold mb-6 text-center text-coral">
            {t('auth.doctor_login_title')}
          </h2>

          {error && <p className="text-red-500 mb-4 text-center">{error}</p>}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
              <FaUserMd className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder={t('auth.username')}
                className="w-full outline-none text-gray-700"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition relative">
              <FaLock className="text-gray-400 mr-2" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.password')}
                className="w-full outline-none text-gray-700 pr-8"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 cursor-pointer text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <button
              type="submit"
              className="w-full bg-coral text-white py-3 rounded-full font-bold shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-all active:scale-95"
            >
              {t('auth.login_button')}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default DoctorLogin;
